import React from 'react';
import Dropdown from './Dropdown';
import { TOOLBAR_DISCLOSURE_TRIGGER } from './Toolbar';

// closeOnSelect rows: a 44px tap target below md (the bottom sheet).
const ROW_TAP = 'min-h-[44px] md:min-h-0 ';

const SELECT_CLS =
  'px-2 py-1.5 text-sm rounded-lg border bg-white dark:bg-gray-700 ' +
  'text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 ' +
  'focus:ring-2 focus:ring-blue-500 focus:outline-none';

/**
 * THE `Group ▾` toolbar control — one disclosure for "how is this list
 * grouped", with an optional built-in SORT section. Extracted 2026-10-02
 * (Gags/teams/tasks Stage 1, spec "Reuse plan": *"extract a generic
 * ui/GroupControl (options passed in; flat-sort built in) used by the
 * Breakdown and My Tasks"*) out of `gags/GagGroupControl.js`, whose DOM it
 * reproduces exactly — that file is now a thin wrapper passing the
 * Breakdown's options, and its tests run unchanged over it.
 *
 * 🔑 IT KNOWS NOTHING ABOUT GAGS OR TASKS. The caller passes:
 *  - `options` [{ id, label }] IN DISPLAY ORDER, `value`, `onChange(id)`;
 *  - `flatId` — the option that means "no groups" (default `'all'`);
 *  - `fallbackLabel` — the trigger's suffix when `value` matches no option;
 *  - `sort` (OPTIONAL) — `{ options, field, dir, onChangeField, onToggleDir }`.
 *    Shown always in flat mode and, in grouped modes, below `md` only (a phone
 *    has no clickable column headers). Omit it and no sort section renders.
 *  - `title` / `titleGrouped` — the trigger tooltip in flat / grouped mode.
 *  - 🔀 `renderLabel(currentLabel)` / `heading` (OPT-IN, 2026-10-02 My Tasks
 *    header round, where the owner renamed Group ▾ to "View by ▾"): replace
 *    the trigger's content and the panel's "Group by" heading. Omitted, the
 *    DOM is byte-identical to before (the Breakdown passes neither). With a
 *    `testId`, each option also carries `${testId}-option-<id>`. `closeOnSelect`
 *    (opt-in) closes the panel on a pick and gives rows a 44px phone target;
 *    `shrinkable` (opt-in, with `renderLabel`) lets the label ellipse.
 * Session state only: it persists nothing (the Breakdown ruling, 2026-09-22,
 * which My Tasks shares — spec: "Not remembered between visits").
 */
export default function GroupControl({
  options,
  value,
  onChange,
  flatId = 'all',
  fallbackLabel = '',
  sort = null,
  title = 'Group the list',
  titleGrouped,
  ariaLabel = 'Group options',
  testId,
  renderLabel = null,
  heading = 'Group by',
  closeOnSelect = false,
  shrinkable = false,
}) {
  const flat = value === flatId;
  const labelFor = Object.fromEntries(options.map((o) => [o.id, o.label]));

  const sortSection = sort ? (
    <>
      <div className="my-2 border-t border-gray-200 dark:border-gray-700" />
      {/* Grouped modes sort WITHIN each group — the grouping fixes the group
          order — so say so, or a phone user picking a column on a grouped list
          sees the groups stay put and reads it as broken. */}
      <div className="px-1 pb-1 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
        {flat ? 'Sort' : 'Sort within groups'}
      </div>
      <div className="flex items-center gap-1.5 px-1">
        <select
          value={sort.field}
          onChange={(e) => sort.onChangeField(e.target.value)}
          aria-label={flat ? 'Flat list sort column' : 'Sort column'}
          className={`${SELECT_CLS} flex-1 min-w-0`}
        >
          {sort.options.map((s) => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={sort.onToggleDir}
          aria-label={`Sort direction: ${sort.dir === 'asc' ? 'ascending' : 'descending'}`}
          title={sort.dir === 'asc' ? 'Ascending — click for descending' : 'Descending — click for ascending'}
          className="px-2 py-1.5 text-sm rounded-lg border bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
        >
          {sort.dir === 'asc' ? '▲' : '▼'}
        </button>
      </div>
    </>
  ) : null;

  return (
    <Dropdown
      align="left"
      panelClassName="w-64 p-2"
      title={flat ? title : (titleGrouped || title)}
      ariaLabel={ariaLabel}
      triggerTestId={testId}
      label={renderLabel ? (
        // Opt-in `shrinkable`: the label ellipses when the caller's row is
        // short of width (the caller lets the trigger's wrapper shrink).
        shrinkable
          ? <span className="min-w-0 truncate">{renderLabel(labelFor[value] || fallbackLabel)}</span>
          : renderLabel(labelFor[value] || fallbackLabel)
      ) : (
        <span className="inline-flex items-center gap-1">
          Group
          <span className="hidden sm:inline text-gray-400 dark:text-gray-500 font-normal">
            · {labelFor[value] || fallbackLabel}
          </span>
        </span>
      )}
      // The GHOST weight (`ui/Toolbar`'s three weights) — never `BTN.secondary`.
      triggerClassName={shrinkable ? `${TOOLBAR_DISCLOSURE_TRIGGER} max-w-full` : TOOLBAR_DISCLOSURE_TRIGGER}
    >
      {({ close }) => (<>
      <div className="px-1 pb-1 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
        {heading}
      </div>
      <ul>
        {options.map((opt) => {
          const active = value === opt.id;
          return (
            <li key={opt.id}>
              <button
                type="button"
                aria-pressed={active}
                onClick={() => { if (closeOnSelect) close(); onChange(opt.id); }}
                data-testid={testId ? `${testId}-option-${opt.id}` : undefined}
                className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 ${closeOnSelect ? ROW_TAP : ''}text-left text-sm rounded-lg transition-colors ${
                  active
                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 font-semibold'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <span>{opt.label}</span>
                {active && <span aria-hidden="true">✓</span>}
              </button>
            </li>
          );
        })}
      </ul>
      {sortSection && (flat ? sortSection : <div className="md:hidden">{sortSection}</div>)}
      </>)}
    </Dropdown>
  );
}
